/**
 * BulkSearchResults — the one bulk search surface, shared by all three entities
 * (payment / artifact / execution) via a descriptor.
 *
 * It owns the id search bar and shared preferences (page size, visible columns),
 * then renders the results as independent per-region tables: one table in a
 * single-region search, or one tab per region in a global search. Each region's
 * table (RegionResultsTable) is self-contained — its own count, in-table status
 * filter, in-table pagination, Refresh, and loading — so regions paginate,
 * filter, and load independently of one another.
 *
 * Search inputs live in Redux (bulkSearchUi): the id list + page size are shared,
 * the page number + filter tokens are per region. Results live in the per-entity
 * search slices. The committed ids mirror into `?ids=` so a search is shareable
 * and survives a refresh. The parent kicks off every region's query on an id /
 * page-size / region change; each table re-runs just its own region when its
 * page or filter changes.
 */
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useStore } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import BreadcrumbGroup from '@cloudscape-design/components/breadcrumb-group';
import Button from '@cloudscape-design/components/button';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter, { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import Tabs from '@cloudscape-design/components/tabs';
import Textarea from '@cloudscape-design/components/textarea';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import type { RootState } from 'src/store/store';
import {
  FilterTokens, resetBulkSearch, setBulkPageSize, setRegionFilterTokens, setRegionPage,
} from 'src/store/slices/bulkSearchUi/bulkSearchUiSlice';
import { addNotification, dismissNotification } from 'src/store/slices/notifications/notificationSlice';
import { RegionPage } from 'src/store/slices/regionFanOut';
import { REGIONS, regionLabel, withRegion } from 'src/constants';
import { BulkSearchDescriptor, parseIds, queryToFilterMap } from './bulkSearchShared';
import './bulkSearch.css';

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 250, label: '250' },
  { value: 500, label: '500' },
  { value: 1000, label: '1000' },
];
const EMPTY_TOKENS: FilterTokens = [];

/** Order-sensitive equality for two id lists. */
const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

/* ------------------------------------------------------------------ */
/* One region's independent table                                      */
/* ------------------------------------------------------------------ */

interface RegionResultsTableProps<TRow> {
  descriptor: BulkSearchDescriptor<TRow>;
  region: string;
  /** Committed, shared id list. */
  ids: string[];
  /** Shared page size. */
  pageSize: number;
  /** Shared visible-column ids. */
  visibleColumns: string[];
  /** This region's fetched page (undefined before its first response). */
  page: RegionPage<TRow> | undefined;
  loading: boolean;
  error: string | undefined;
  /** "container" for a standalone single-region table; "embedded" inside a region tab. */
  variant: 'container' | 'embedded';
  /** Heading text for the single-region table (unused in a tab). */
  heading: string;
  /** Shared preferences control (page size + columns), rendered beside the pager. */
  preferences: ReactNode;
}

function RegionResultsTable<TRow>({
  descriptor, region, ids, pageSize, visibleColumns, page, loading, error, variant, heading,
  preferences,
}: RegionResultsTableProps<TRow>) {
  const dispatch = useAppDispatch();
  const pageNumber = useAppSelector((s) => s.bulkSearchUi.byRegion[region]?.pageNumber ?? 1);
  const filterTokens = useAppSelector((s) => s.bulkSearchUi.byRegion[region]?.filterTokens) ?? EMPTY_TOKENS;
  // True only while a refresh this table triggered is in flight, so paging/filtering
  // does not grey out the Refresh button.
  const [refreshing, setRefreshing] = useState(false);

  const query = useMemo<PropertyFilterProps.Query>(
    () => ({ operation: 'and', tokens: filterTokens }), [filterTokens],
  );
  const filters = useMemo(() => queryToFilterMap(query), [query]);

  const items = useMemo(() => page?.items ?? [], [page]);
  const total = page?.total ?? 0;
  const dynamicColumns = useMemo(() => descriptor.dynamicColumns?.(items) ?? [], [descriptor, items]);
  const allColumns = useMemo(() => [...descriptor.columns, ...dynamicColumns], [descriptor, dynamicColumns]);
  const activeColumns = allColumns.filter((c) => c.id && visibleColumns.includes(c.id));
  const pagesCount = Math.max(1, Math.ceil(total / pageSize));
  // Loading until this region's first page has arrived (page === undefined), or
  // while a page/filter change is in flight — but an error ends the load and shows
  // in the empty slot instead of a spinner.
  const showLoading = (loading || (ids.length > 0 && page === undefined)) && !error;
  // Drop the refresh-in-flight flag once this region finishes loading.
  useEffect(() => { if (!loading) setRefreshing(false); }, [loading]);

  // Mirror this region's failure into the global Flashbar (the detailed message
  // lives there, not in the table). The cleanup dismisses it, so it clears when a
  // new query starts (which clears the error), and when the page unmounts.
  useEffect(() => {
    const nid = `search-error:${descriptor.entity}:${region}`;
    if (error) {
      dispatch(addNotification({
        id: nid,
        type: 'error',
        header: `Couldn't load ${descriptor.entity}s in ${regionLabel(region)}`,
        content: error,
      }));
    }
    return () => { dispatch(dismissNotification(nid)); };
  }, [error, region, descriptor.entity, dispatch]);

  // Per-region changes re-run only this region (page/filter are server-side).
  const onPageChange = (next: number) => {
    dispatch(setRegionPage({ region, page: next }));
    descriptor.runRegion(dispatch, { region, ids, pageNumber: next, pageSize, filters });
  };
  const onFilterChange = (tokens: FilterTokens) => {
    dispatch(setRegionFilterTokens({ region, tokens }));
    descriptor.runRegion(dispatch, {
      region, ids, pageNumber: 1, pageSize, filters: queryToFilterMap({ operation: 'and', tokens }),
    });
  };
  const onRefresh = () => {
    if (ids.length === 0) return;
    setRefreshing(true);
    descriptor.runRegion(dispatch, { region, ids, pageNumber, pageSize, filters });
  };

  const empty = (
    <Box textAlign="center" padding={{ vertical: 'l' }}>
      <SpaceBetween size="s" alignItems="center">
        {error ? (
          <StatusIndicator type="error">Couldn&apos;t load {descriptor.entity}s</StatusIndicator>
        ) : (
          <Box color="text-body-secondary">No {descriptor.entity}s match the current filters.</Box>
        )}
        {error && <Button iconName="refresh" onClick={onRefresh}>Retry</Button>}
      </SpaceBetween>
    </Box>
  );

  return (
    <Table
      variant={variant}
      resizableColumns
      sortingDisabled
      trackBy="id"
      loading={showLoading}
      loadingText={`Searching ${descriptor.entity}s…`}
      items={items}
      columnDefinitions={activeColumns}
      header={
        // Single-region table carries the heading + count; a region tab shows those
        // in its tab label and has no table header, so the content sits flush.
        variant === 'container' ? (
          <Header
            variant="h2"
            counter={`(${items.length.toLocaleString()} of ${total.toLocaleString()})`}
          >
            {heading}
          </Header>
        ) : undefined
      }
      filter={
        <PropertyFilter
          query={query}
          onChange={({ detail }) => onFilterChange([...detail.tokens])}
          filteringProperties={descriptor.filteringProperties}
          countText={`${query.tokens.length} filter${query.tokens.length === 1 ? '' : 's'}`}
          filteringPlaceholder={`Filter ${descriptor.entity}s by status, state`}
          expandToViewport
        />
      }
      pagination={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="icon"
            ariaLabel={`Refresh ${regionLabel(region)}`}
            onClick={onRefresh}
            disabled={refreshing}
          />
          <Pagination
            currentPageIndex={pageNumber}
            pagesCount={pagesCount}
            onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
          />
        </SpaceBetween>
      }
      preferences={preferences}
      empty={empty}
    />
  );
}

/* ------------------------------------------------------------------ */
/* The bulk search surface                                             */
/* ------------------------------------------------------------------ */

export function BulkSearchResults<TRow>({ descriptor }: { descriptor: BulkSearchDescriptor<TRow> }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const store = useStore<RootState>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Shared search inputs (Redux). Entity-guarded: ignore another entity's lingering state.
  const ui = useAppSelector((s) => s.bulkSearchUi, shallowEqual);
  const forThisEntity = ui.entity === descriptor.entity;
  const ids = useMemo(() => (forThisEntity ? ui.ids : []), [forThisEntity, ui.ids]);
  const pageSize = ui.pageSize;

  // Per-region results (one subscription; passed down to each table).
  const { regionResults, loadingRegions, regionErrors } = useAppSelector(descriptor.select, shallowEqual);
  const { region, global } = useAppSelector((s) => s.env, shallowEqual);

  const [draft, setDraft] = useState(() => ids.join('\n'));
  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    descriptor.defaultVisibleColumns ?? descriptor.columns.map((c) => c.id!).filter(Boolean),
  );
  const [regionTab, setRegionTab] = useState<string | null>(null);

  const hasIds = ids.length > 0;
  const targetRegions = useMemo(() => (global ? REGIONS.map((r) => r.value) : [region]), [global, region]);

  // Seed Redux from the URL: on mount, entity switch, and when ?ids= changes from
  // outside (navigating in from the search page). In-page Search updates Redux and
  // the URL together, so those stay in step and never loop here.
  useEffect(() => {
    const urlIds = parseIds((searchParams.get('ids') ?? '').replace(/,/g, '\n'));
    if (!forThisEntity || !sameIds(urlIds, ids)) {
      descriptor.clear(dispatch);
      dispatch(resetBulkSearch({ entity: descriptor.entity, ids: urlIds }));
    }
  }, [dispatch, descriptor.entity, forThisEntity, ids, searchParams]);

  // Keep the textarea in step with the committed ids (URL seed / entity switch).
  useEffect(() => { setDraft(ids.join('\n')); }, [ids]);

  // Kick off every target region's query on an id / page-size / region-set change.
  // Reads the current per-region page/filter lazily so a single region's page change
  // (handled in its own table) does not re-fetch the others.
  useEffect(() => {
    if (ids.length === 0) return;
    const byRegion = store.getState().bulkSearchUi.byRegion;
    targetRegions.forEach((rgn) => {
      const ru = byRegion[rgn];
      descriptor.runRegion(dispatch, {
        region: rgn,
        ids,
        pageNumber: ru?.pageNumber ?? 1,
        pageSize,
        filters: queryToFilterMap({ operation: 'and', tokens: ru?.filterTokens ?? [] }),
      });
    });
  }, [descriptor, dispatch, store, ids, pageSize, targetRegions]);

  // Clear server state on unmount so a return visit starts clean.
  useEffect(() => () => { descriptor.clear(dispatch); }, [descriptor, dispatch]);

  // Column toggle list spans the fixed columns + every ref-type column seen across
  // regions, so a dynamic column stays toggleable even if only one region has it.
  const allItems = useMemo(
    () => regionResults.reduce<TRow[]>((all, p) => all.concat(p.items), []), [regionResults],
  );
  const dynamicColumns = useMemo(() => descriptor.dynamicColumns?.(allItems) ?? [], [descriptor, allItems]);
  const visibleContentOptions = [...descriptor.columns, ...dynamicColumns].map((c) => ({
    id: c.id!,
    label: typeof c.header === 'string' ? c.header : c.id!,
  }));

  const regionData = (rgn: string) => ({
    page: regionResults.find((p) => p.region === rgn),
    loading: loadingRegions.includes(rgn),
    error: regionErrors.find((e) => e.region === rgn)?.message,
  });

  const runSearch = () => {
    const parsed = parseIds(draft);
    descriptor.clear(dispatch);
    dispatch(resetBulkSearch({ entity: descriptor.entity, ids: parsed }));
    const next = new URLSearchParams(searchParams);
    if (parsed.length) next.set('ids', parsed.join(',')); else next.delete('ids');
    setSearchParams(next);
  };

  const breadcrumbs = (
    <BreadcrumbGroup
      items={[
        { text: 'Search', href: withRegion('/', region) },
        { text: descriptor.title, href: withRegion(descriptor.path, region) },
      ]}
      onFollow={(e) => { e.preventDefault(); if (e.detail.href !== '#') navigate(e.detail.href); }}
    />
  );

  const searchBar = (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div className="bulk-search-input" style={{ flex: 1, minWidth: 0 }}>
        <Textarea
          value={draft}
          onChange={({ detail }) => setDraft(detail.value)}
          placeholder={descriptor.bulkPlaceholder}
          rows={3}
        />
      </div>
      <div style={{ flexShrink: 0 }}>
        <Button variant="primary" iconName="search" onClick={runSearch}>
          Search
        </Button>
      </div>
    </div>
  );

  const preferencesControl = (
    <CollectionPreferences
      title="Preferences"
      confirmLabel="Confirm"
      cancelLabel="Cancel"
      preferences={{ pageSize, visibleContent: visibleColumns }}
      onConfirm={({ detail }) => {
        if (detail.pageSize && detail.pageSize !== pageSize) dispatch(setBulkPageSize(detail.pageSize));
        if (detail.visibleContent) setVisibleColumns([...detail.visibleContent]);
      }}
      pageSizePreference={{ title: 'Page size', options: PAGE_SIZE_OPTIONS }}
      visibleContentPreference={{
        title: 'Visible columns',
        options: [{ label: `${descriptor.title} columns`, options: visibleContentOptions }],
      }}
    />
  );

  // Global: a tab per region that is loading, has rows, or errored. During the
  // initial load every target region is loading, so all appear; empty regions
  // drop out once their response lands.
  const visibleRegions = targetRegions.filter((rgn) => {
    const d = regionData(rgn);
    return d.loading || (d.page?.total ?? 0) > 0 || !!d.error;
  });
  const activeRegionTab = visibleRegions.includes(regionTab ?? '')
    ? regionTab!
    : visibleRegions[0];
  // Every target region has reported back (so "no matches" is real, not pre-fetch).
  const allFetched = targetRegions.every((rgn) => regionResults.some((p) => p.region === rgn));

  const initialSpinner = (
    <Box textAlign="center" padding={{ vertical: 'xl' }}>
      <SpaceBetween size="s" alignItems="center">
        <Spinner size="large" />
        <Box color="text-body-secondary">Searching {descriptor.entity}s…</Box>
      </SpaceBetween>
    </Box>
  );

  let results;
  if (!hasIds) {
    results = (
      <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
        Enter one or more {descriptor.entity} ids above and choose Search.
      </Box>
    );
  } else if (!global) {
    const d = regionData(region);
    results = (
      <RegionResultsTable
        descriptor={descriptor}
        region={region}
        ids={ids}
        pageSize={pageSize}
        visibleColumns={visibleColumns}
        page={d.page}
        loading={d.loading}
        error={d.error}
        variant="container"
        heading={regionLabel(region)}
        preferences={preferencesControl}
      />
    );
  } else if (visibleRegions.length > 0) {
    results = (
      <Tabs
        variant="container"
        activeTabId={activeRegionTab}
        onChange={({ detail }) => setRegionTab(detail.activeTabId)}
        tabs={visibleRegions.map((rgn) => {
          const d = regionData(rgn);
          return {
            id: rgn,
            // Region + "current of total" lives in the tab label (not a second table header).
            label: d.page
              ? `${regionLabel(rgn)} (${d.page.items.length.toLocaleString()} of ${d.page.total.toLocaleString()})`
              : regionLabel(rgn),
            content: (
              <RegionResultsTable
                descriptor={descriptor}
                region={rgn}
                ids={ids}
                pageSize={pageSize}
                visibleColumns={visibleColumns}
                page={d.page}
                loading={d.loading}
                error={d.error}
                variant="embedded"
                heading={regionLabel(rgn)}
                preferences={preferencesControl}
              />
            ),
          };
        })}
      />
    );
  } else if (allFetched) {
    results = (
      <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
        No {descriptor.entity}s matched in any of the {targetRegions.length} regions.
      </Box>
    );
  } else {
    results = initialSpinner;
  }

  return (
    <Box padding="l">
      <SpaceBetween size="l">
        {breadcrumbs}
        <Container
          header={<Header variant="h2">{descriptor.title}</Header>}
        >
          <SpaceBetween size="m">
            {searchBar}
            {results}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Box>
  );
}
